import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { Link, useHistory } from 'react-router-dom';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { DropdownItem } from '../../../web/components-v2/Dropdown';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { NavCompactToggle, NavFooter, NavTooltip, NavUserMenu } from '../../../web/components/Happiness/NavFooter';
import { NavBarLink } from '../../../web/components/Happiness/NavBarLink';
import { styleSheet as navLinkStyleSheet } from '../../../web/components/Happiness/NavBarLink/styles';
import { styleSheet as navStyleSheet } from '../../../web/components/Happiness/NavBar/styles';
import { AlarmClockIcon } from '../../../web/components/svgs/icons/AlarmClockIcon';
import { ApproveMarkIcon } from '../../../web/components/svgs/icons/ApproveMarkIcon';
import { AssessmentReportIcon } from '../../../web/components/svgs/icons/AssessmentReportIcon';
import { CalendarDateIcon } from '../../../web/components/svgs/icons/CalendarDateIcon';
import { CallLogIcon } from '../../../web/components/svgs/icons/CallLogIcon';
import { FollowUpsIcon } from '../../../web/components/svgs/icons/FollowUpsIcon';
import { IntegrationIcon } from '../../../web/components/svgs/icons/IntegrationIcon';
import { LogoutIcon } from '../../../web/components/svgs/icons/LogoutIcon';
import { NavIconWrapper } from '../../../web/components/svgs/icons/NavIcon';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { QuestionMarkIcon } from '../../../web/components/svgs/icons/QuestionMarkIcon';
import { ReportingIcon } from '../../../web/components/svgs/icons/ReportingIcon';
import { SearchIcon } from '../../../web/components/svgs/icons/SearchIcon';
import { SettingsIcon } from '../../../web/components/svgs/icons/SettingsIcon';
import { useSearch } from '../../contexts/search';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { isCoffeeMobileRoute, useIsCoffeeMobile } from '../../shell/CoffeeMobileRouteGuard';
import { white } from '../../styles/colors';
import { getTimeTrackingNavItems } from '../../timeTracking/navItems';
import { useTimeTracking } from '../../timeTracking/useTimeTracking';
import coffeeCupLogo from '../../assets/coffee-cup-logo.svg';
import { CoffeeLogo } from '../svgs/CoffeeLogo';
import { TrophyIcon } from '../svgs/icons/TrophyIcon';
import { styleSheet } from './styles';

type CoffeeNavItem = {
	linkName: string;
	path: string | string[];
	icon: React.ReactNode;
	badge?: number;
	ariaLabel?: string;
};

const TIME_TRACKING_ICONS = {
	hoursReview: <ApproveMarkIcon color={white} height={18} width={19} />,
	timesheet: <AlarmClockIcon color={white} height={20} width={20} />,
};

const NAV_ITEMS: CoffeeNavItem[] = [
	{
		linkName: 'Queue',
		// `/queue` redirects to `/focused-view` for reps who opted in, so the link stays lit there too.
		path: ['/queue', '/focused-view'],
		icon: <PhoneIcon fill={white} />,
	},
	{
		linkName: 'Call Log',
		path: '/call-log',
		icon: <CallLogIcon fill={white} />,
	},
	{
		linkName: 'Demos',
		path: '/demos',
		icon: <CalendarDateIcon fillColor={white} />,
	},
	{
		linkName: 'Follow Ups',
		path: '/follow-ups',
		icon: <FollowUpsIcon fill={white} />,
	},
	{
		linkName: 'Leaderboard',
		path: '/leaderboard',
		icon: <TrophyIcon fillColor={white} />,
	},
];

const COACHING_REVIEW_ITEM: CoffeeNavItem = {
	linkName: 'Coaching Review',
	path: '/ai-coaching-review',
	icon: <AssessmentReportIcon fillColor={white} />,
};

const SETTINGS_ITEM: CoffeeNavItem = {
	linkName: 'Settings',
	path: '/settings',
	icon: <SettingsIcon stroke={white} />,
};

const REPORTING_PATH = '/reporting';
const INTEGRATION_PATH = '/integration';

// The menu otherwise shrinks svgs to 12px; NavIconWrapper sizes them like the Levitate menu icons.
const MENU_ICON_SIZE = 20;

const MenuItemContent = ({ icon, label }: { icon: JSX.Element; label: string }) => (
	<span className={css(styleSheet.menuItem)}>
		<NavIconWrapper width={MENU_ICON_SIZE}>{icon}</NavIconWrapper>
		{label}
	</span>
);

function _CoffeeNavBar({
	compactLayout,
	onSearch,
	onToggleCompact,
}: {
	compactLayout: boolean;
	onSearch?: () => void;
	onToggleCompact?: () => void;
}) {
	const userSession = useUserSession();
	const history = useHistory();
	const { setShowSearch } = useSearch();
	const isMobile = useIsCoffeeMobile();
	const { hasManagerPermissions } = useManagerPermissions();

	const timeTracking = useTimeTracking();
	const timeTrackingItems: CoffeeNavItem[] = getTimeTrackingNavItems(timeTracking).map(item => ({
		ariaLabel: item.ariaLabel,
		badge: item.badge,
		icon: TIME_TRACKING_ICONS[item.key],
		linkName: item.name,
		path: item.path,
	}));
	const allItems = [
		...NAV_ITEMS,
		...(hasManagerPermissions ? [COACHING_REVIEW_ITEM] : []),
		...timeTrackingItems,
		SETTINGS_ITEM,
	];
	// Filter by where the link goes (the first path), not where it stays lit: Queue goes to `/queue`, which
	// a phone can't show, even though `/focused-view` is allowed.
	const items = isMobile
		? allItems.filter(({ path }) => isCoffeeMobileRoute(Array.isArray(path) ? path[0] : path))
		: allItems;
	const showMenuRoute = (path: string) => !isMobile || isCoffeeMobileRoute(path);
	const isPropertyMappingEnabled = userSession?.account?.features?.aida?.propertyMappingEnabled;

	const renderLink = ({ linkName, path, icon, badge, ariaLabel }: CoffeeNavItem) => (
		<NavTooltip key={linkName} disabled={!compactLayout} label={linkName}>
			<NavBarLink
				accessory={
					badge ? (
						<div
							aria-label={ariaLabel}
							className={css(
								compactLayout ? navStyleSheet.compactUnreadTextMessageCount : navStyleSheet.unreadTextMessageCount
							)}
						>
							{badge > 99 ? '99+' : badge}
						</div>
					) : null
				}
				activeLinkStyles={[navStyleSheet.linkActive, !compactLayout && navStyleSheet.expandedTabIndicator]}
				compactLayout={compactLayout}
				focusOnTabOnly
				icon={icon}
				linkName={linkName}
				labelAsTooltip={true}
				linkStyles={[navStyleSheet.link, compactLayout && navStyleSheet.linkCompact]}
				path={path}
			/>
		</NavTooltip>
	);

	return (
		<>
			<nav
				className={`coffee-nav-bar ${css(navStyleSheet.nav, !compactLayout && navStyleSheet.navExpanded, styleSheet.nav)}`}
				aria-label='Coffee navigation'
			>
				<div className={css(navStyleSheet.header)}>
					<Link to='/queue' aria-label='Coffee home' className={css(styleSheet.logoLink)}>
						{compactLayout ? (
							<img src={coffeeCupLogo} alt='' className={css(styleSheet.logoCompact)} />
						) : (
							<CoffeeLogo className={css(styleSheet.logo)} />
						)}
					</Link>
				</div>
				<div className={css(navStyleSheet.links)}>
					{items.map(renderLink)}
					{/* Search opens the legacy modal rather than routing, so it cannot go through NavBarLink. */}
					{!isMobile && (
						<NavTooltip disabled={!compactLayout} label='Search'>
							<Button
								type='button'
								variant='ghost'
								aria-label='Search'
								onClick={onSearch ?? (() => setShowSearch(value => !value))}
								styles={[
									navStyleSheet.link,
									styleSheet.searchButton,
									compactLayout && navStyleSheet.linkCompact,
									navLinkStyleSheet.linkFocusOnTabOnly,
								]}
							>
								<span className={css(navLinkStyleSheet.clickFocusTarget)} tabIndex={-1}>
									<div className={css(navLinkStyleSheet.icon)}>
										<SearchIcon fillColor={white} />
									</div>
									{!compactLayout && <span className={css(navLinkStyleSheet.linkName)}>Search</span>}
								</span>
							</Button>
						</NavTooltip>
					)}
				</div>
				<NavFooter>
					<NavUserMenu compactLayout={compactLayout}>
						<DropdownItem onClick={() => window.open('https://help.coffee.inc', '_blank', 'noreferrer')}>
							<MenuItemContent
								icon={<QuestionMarkIcon fillColor={THEME.colors.icon.gray} className={css(styleSheet.menuIcon)} />}
								label='Help'
							/>
						</DropdownItem>
						{showMenuRoute(REPORTING_PATH) && (
							<DropdownItem onClick={() => history.push(REPORTING_PATH)}>
								<MenuItemContent
									icon={<ReportingIcon fill={THEME.colors.icon.gray} className={css(styleSheet.menuIcon)} />}
									label='Reporting'
								/>
							</DropdownItem>
						)}
						{isPropertyMappingEnabled && showMenuRoute(INTEGRATION_PATH) && (
							<DropdownItem onClick={() => history.push(INTEGRATION_PATH)}>
								<MenuItemContent
									icon={<IntegrationIcon fill={THEME.colors.icon.gray} className={css(styleSheet.menuIcon)} />}
									label='Integration'
								/>
							</DropdownItem>
						)}
						<DropdownItem onClick={() => history.push('/logout')}>
							<MenuItemContent
								icon={<LogoutIcon fill={THEME.colors.icon.gray} className={css(styleSheet.menuIcon)} />}
								label='Log out'
							/>
						</DropdownItem>
					</NavUserMenu>
				</NavFooter>
			</nav>
			{onToggleCompact && (
				<div className={css(styleSheet.toggleSlot)}>
					<NavCompactToggle compactLayout={compactLayout} onToggle={onToggleCompact} />
				</div>
			)}
		</>
	);
}

export const CoffeeNavBar = observer(_CoffeeNavBar);
